<script setup lang="ts">
// @ts-expect-error Not a ts file
import store from '@/store'
import TicketLeft from '@/features/tickets/assets/TicketLeft.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { useImage } from '@/shared/composables/image'
import { ModalWrapper } from '@/shared/ui/modals'
import { computed, ref } from 'vue'
import MyTicketsInterface from '@/features/tickets/ui/MyTicketsInterface.vue'
import { useEventQuery } from '@/enitites/event/api/queries'

const props = defineProps<{
  eventId: number
}>()

const getUserDB = computed(() => store.getters.getUserDB)
const { getImageById } = useImage()
const { data: event } = useEventQuery(props.eventId)

const tickets = computed(() => event.value?.paymentSystem?.tickets)
const isHost = computed(
  () => event.value?.permissions.canEdit || event.value?.staff.hosts?.some(({ id }) => id === getUserDB.value?.id)
)

const isShowModal = ref(false)
const selectedTicketId = ref<number>()

const handleOpenModal = (ticketId: number) => {
  selectedTicketId.value = ticketId
  isShowModal.value = true
}
</script>

<template>
  <div
    v-if="!isHost"
    class="event-purchased-tickets"
  >
    <p class="section-title">Your tickets</p>
    <ul class="purchased-tickets-list">
      <li
        v-for="ticket in tickets"
        :key="ticket.id"
        class="purchased-ticket-item"
        tabindex="0"
        @click="handleOpenModal(ticket.id)"
        @keydown.enter="handleOpenModal(ticket.id)"
      >
        <TicketLeft class="ticket-left" />
        <div class="ticket-main">
          <span class="ticket-divider" />
          <div class="ticket-info">
            <p class="ticket-title">{{ ticket.title }}</p>
            <div class="ticket-assignment">
              <AvatarPlaceholder
                v-if="!ticket.assignedTo || !('avatar' in ticket.assignedTo) || !ticket.assignedTo.avatar"
              />
              <v-avatar
                v-else
                size="20"
              >
                <v-img :src="getImageById(ticket.assignedTo.avatar, 40, 40, 'avatar')" />
              </v-avatar>
              <span class="assigned-to-name">
                {{
                  !ticket.assignedTo
                    ? 'Unassigned'
                    : 'dancerName' in ticket.assignedTo
                      ? ticket.assignedTo.dancerName
                      : ticket.assignedTo.email || 'Unnamed User'
                }}
              </span>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <ModalWrapper
      :is-show="isShowModal"
      modal-width="904px"
      @update:isShow="isShowModal = $event"
    >
      <MyTicketsInterface
        v-if="isShowModal"
        :event-id="eventId"
        :selected-ticket-id="selectedTicketId"
      />
    </ModalWrapper>
  </div>
</template>

<style scoped lang="scss">
.event-purchased-tickets {
  display: flex;
  flex-direction: column;
  gap: 10px;

  p,
  ul,
  ol {
    margin: 0;
    padding: 0;
  }

  .section-title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: var(--color-dark-800);
  }

  .purchased-tickets-list {
    display: flex;
    gap: 20px;
    overflow-x: auto;

    .purchased-ticket-item {
      display: flex;
    }

    .ticket-left {
      flex: 0 0 auto;
      color: var(--color-gold-800);
    }

    .ticket-main {
      position: relative;
      display: flex;
      align-items: center;
      background-color: var(--color-gold-800);
      border-radius: 0 8px 8px 0;
      padding: 19px 16px 19px 25px;
      margin-left: -1px;
      width: max-content;
      height: 80px;

      .ticket-divider {
        position: absolute;
        left: 5px;
        top: 50%;
        transform: translateY(-50%);
        height: 67px;
        border-left: 2px dashed var(--color-gold-900);
      }

      .ticket-info {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .ticket-title {
          font-size: 20px;
          line-height: 14px;
          font-weight: 500;
          color: #4e3e0e;
        }

        .ticket-assignment {
          display: flex;
          align-items: center;
          gap: 6px;

          .assigned-to-name {
            font-size: 12px;
            line-height: 14px;
            color: #4e3e0e;
          }
        }
      }
    }
  }
}
</style>
